"use client"

import type React from "react"
import { useState } from "react"
import type { ProductoConPrecioResponse, SugerenciaResponse } from "./types"
import { buscarArticulo, buscarPorRefProveedor } from "./services/api"
import '@fortawesome/fontawesome-free/css/all.min.css';
import "./App.css"


const App: React.FC = () => {
  const [searchInput, setSearchInput] = useState("")
  const [loading, setLoading] = useState(false)
  const [producto, setProducto] = useState<ProductoConPrecioResponse | null>(null)
  const [sugerencias, setSugerencias] = useState<SugerenciaResponse[]>([])
  const [error, setError] = useState<string | null>(null)

  const limpiarResultados = () => {
    setProducto(null)
    setSugerencias([])
    setError(null)
  }

  const mostrarError = (mensaje: string) => {
    setError(mensaje)
    setProducto(null)
    setSugerencias([])
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value.toUpperCase())
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      buscarProducto()
    }
  }

  const buscarProducto = async () => {
    const articulo = searchInput.trim()

    if (!articulo) {
      mostrarError("Por favor ingresa un artículo para buscar")
      return
    }

    limpiarResultados()
    setLoading(true)

    try {
      const response = await buscarArticulo({ Articulo: articulo })

      if (response.success) {
        if (response.tipo === "producto" && response.data) {
          setProducto(response.data as ProductoConPrecioResponse)
          setSearchInput("")
        } else if (response.tipo === "sugerencias" && response.data) {
          setSugerencias(response.data as SugerenciaResponse[])
        }
      } else {
        mostrarError(response.message || "No se encontraron resultados")
      }
    } catch (error) {
      console.error("Error de conexión:", error)
      mostrarError("Error de conexión. Por favor intenta nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  const buscarPorSugerencia = async (refProveedor: string) => {
    limpiarResultados()
    setLoading(true)

    try {
      const response = await buscarPorRefProveedor({ RefProveedor: refProveedor })

      if (response.success && response.data) {
        setProducto(response.data as ProductoConPrecioResponse)
      } else {
        mostrarError(response.message || "Artículo no encontrado")
      }
    } catch (error) {
      console.error("Error de conexión:", error)
      mostrarError("Error de conexión. Por favor intenta nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="container">
      <div className="main-card">
        <div className="card-content">
          {/* Columna 1 */}
          <div className="column-left">
            <div className="logo-container">
              <img src="/images/logo-empresa.png" alt="Logo Empresa" className="company-logo" />
            </div>
            <div className="info-text">
              <div style={{ display: "flex", alignItems: "center" }}>
                <img
                  src="https://cdn-icons-png.flaticon.com/512/8281/8281549.png"
                  alt="Estados Unidos"
                  style={{ width: "50px", marginRight: "10px" }}
                />
                <h2>Estados Unidos</h2>
              </div>
              <h3>Verificador de precios</h3>
            </div>
          </div>

          {/* Columna 2 */}
          <div className="column-right">
            <div className="search-section">
              <p className="explanation-text">
                Ingresa el SKU, código de barras o descripción del artículo que deseas consultar.
              </p>

              <div className="search-container">
                <div className="input-group">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Ingresa SKU, código de barras o descripción..."
                    maxLength={100}
                    value={searchInput}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                  />
                  <button className="search-button" onClick={buscarProducto} disabled={loading}>
                    <i className="fas fa-search"></i>
                    Buscar
                  </button>
                </div>
              </div>

              {/* Loading spinner */}
              {loading && (
                <div className="loading-spinner">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Buscando artículo...</span>
                </div>
              )}

              {/* Resultado del producto */}
              {producto && (
                <div className="product-result">
                  <h4>Producto encontrado:</h4>
                  <div className="product-info">
                    <p>
                      <strong>Código:</strong> <span>{producto.CodigoFinal}</span>
                    </p>
                    <p>
                      <strong>Descripción:</strong> <span>{producto.DescripcionFinal}</span>
                    </p>
                    <p>
                      <strong>Precio:</strong>{" "}
                      <span
                        id="productPrice" className={producto.Precio && producto.Precio > 0 ? "price-highlight price-available" : "price-unavailable"}
                      >
                        {producto.PrecioFormateado}
                      </span>
                    </p>
                  </div>

                  {/* Sección de márgenes de ganancia */}
                  {producto.Precio && producto.Precio > 0 && (
                    <div className="margin-section">
                      <h5>Precios con margen de ganancia:</h5>
                      <div className="margin-grid">
                        <div className="margin-item margin-50">
                          <span className="margin-label">50% ganancia:</span>
                          <span className="margin-price">{producto.PrecioMargen50Formateado} USD</span>
                        </div>
                        <div className="margin-item margin-70">
                          <span className="margin-label">70% ganancia:</span>
                          <span className="margin-price">{producto.PrecioMargen70Formateado} USD</span>
                        </div>
                        <div className="margin-item margin-100">
                          <span className="margin-label">100% ganancia:</span>
                          <span className="margin-price">{producto.PrecioMargen100Formateado} USD</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Sugerencias */}
              {sugerencias.length > 0 && (
                <div className={`suggestions-container ${sugerencias.length > 4 ? "has-scroll" : ""}`}>
                  <h4>Selecciona un artículo sugerido:</h4>
                  <div className="suggestions-list">
                    {sugerencias.map((sugerencia, index) => (
                      <div
                        key={index}
                        className="suggestion-card"
                        onClick={() => buscarPorSugerencia(sugerencia.RefProveedorFinal)}
                      >
                        <p>
                          <strong>SKU:</strong> {sugerencia.RefProveedorFinal}
                        </p>
                        <p>
                          <strong>Descripción:</strong> {sugerencia.DescripcionFinal}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Mensaje de error */}
              {error && (
                <div className="error-message">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>{error}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}


export default App
